/**
 * GM Hazard Lab — spawn race hazards NEXT TO YOU, outside any event, for
 * clean isolated testing (DJ ask 2026-07-09: "objects with scripts like
 * that as something we can spawn in our debug card").
 *
 * UI over the `debugHazardSpawn` / `debugHazardClear` room messages
 * (DebugHazardLabService, gmLevel ≥ 4). Spawned hazards are driven by the
 * REAL GmEventHazardEngine — same moving hit shapes (raceHazardGeometry),
 * same impulse broadcasts, same renderers and 🏁 debug overlay as a live
 * race, so what you verify here is exactly what races run.
 *
 * Tip: turn on the Race Debug overlay's Hazards toggle to see the live hit
 * shapes (red = can hit you right now; grey = authored envelope).
 */
import React from 'react';
import {
  getRoom,
  useRoomPulse,
  useSelfGmLevel,
  GmLevelChip,
  gmBtnStyle,
  gmSectionTitleStyle,
} from './gmShared';

const KINDS = [
  { kind: 'swing_hammer', label: '🔨 Hammer', hint: 'rotating arm capsule' },
  { kind: 'falling_rock', label: '🪨 Rock', hint: 'falls, resets, falls' },
  { kind: 'bounce_pad', label: '🟢 Bounce Pad', hint: 'one-shot launch' },
  { kind: 'conveyor_belt', label: '➡️ Conveyor', hint: 'carries you (+150)' },
  { kind: 'fan_updraft', label: '🌀 Fan', hint: 'updraft column' },
  { kind: 'ice_patch', label: '🧊 Ice', hint: 'low-friction strip' },
  { kind: 'rotating_bar', label: '🪓 Spinner', hint: 'full-360 sweeping bar' },
  { kind: 'crusher', label: '🔩 Crusher', hint: 'telegraphed piston slam' },
  { kind: 'pendulum_axe', label: '⛓️ Pendulum', hint: 'wide swing; spawn 3 for a staggered row' },
  { kind: 'flame_jet', label: '🔥 Flame Jet', hint: 'duty-cycle burst column' },
] as const;

const MIN_GM = 4;

const PHASE_STEPS = [
  { quarters: 0, label: '0' },
  { quarters: 1, label: '¼' },
  { quarters: 2, label: '½' },
  { quarters: 3, label: '¾' },
] as const;

export const GmHazardLabContent: React.FC = () => {
  useRoomPulse();
  const gmLevel = useSelfGmLevel();
  const connected = !!getRoom();
  const allowed = connected && gmLevel >= MIN_GM;
  // Phase stepper (O.6): offset applied to the NEXT spawn, in quarters of
  // the kind's period — how stagger-feel gets tuned in the lab.
  const [phaseQuarters, setPhaseQuarters] = React.useState<0 | 1 | 2 | 3>(0);

  const spawn = (kind: string) => {
    getRoom()?.send('debugHazardSpawn', { kind, phaseQuarters });
  };
  const clear = () => {
    getRoom()?.send('debugHazardClear', {});
  };

  return (
    <div style={{ padding: 10, color: '#ddd', fontSize: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <GmLevelChip gmLevel={gmLevel} />
        {!connected && <span style={{ color: '#e74c3c' }}>not connected</span>}
        {connected && !allowed && <span style={{ color: '#e74c3c' }}>needs GM {MIN_GM}+</span>}
      </div>

      <div style={gmSectionTitleStyle}>Spawn at your position (180px ahead)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
        {KINDS.map(({ kind, label, hint }) => (
          <button
            key={kind}
            style={{ ...gmBtnStyle, opacity: allowed ? 1 : 0.4 }}
            disabled={!allowed}
            onClick={() => spawn(kind)}
            title={hint}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={gmSectionTitleStyle}>Phase offset (next spawn, × period)</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {PHASE_STEPS.map(({ quarters, label }) => (
          <button
            key={quarters}
            data-testid={`hazard-lab-phase-${quarters}`}
            style={{
              ...gmBtnStyle,
              flex: 1,
              opacity: allowed ? 1 : 0.4,
              outline: phaseQuarters === quarters ? '2px solid #f1c40f' : 'none',
            }}
            disabled={!allowed}
            onClick={() => setPhaseQuarters(quarters)}
            title={`Next spawn starts ${label} of its period into the cycle`}
          >
            {label}
          </button>
        ))}
      </div>

      <button
        style={{ ...gmBtnStyle, width: '100%', opacity: allowed ? 1 : 0.4 }}
        disabled={!allowed}
        onClick={clear}
      >
        🧹 Clear all lab hazards
      </button>

      <div style={{ marginTop: 10, color: '#8a93a6', lineHeight: 1.5 }}>
        Runs the real race hazard engine — moving hit shapes, impulses,
        renderers, and the 🏁 Race Debug overlay all behave exactly as in a
        live race. Spawn multiple to build a gauntlet; phases restart on
        every spawn.
      </div>
    </div>
  );
};
